import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";
import CommonButton from "components/commonButton/CommonButton";
import BackDrop from "components/backDrop/BackDrop";
import {
  getNotificationRecipients,
  clearNotificationRecipients,
  sendNotifications,
} from "redux/actions/reservationAction";
import "../../../src/assets/styles/NotifyRecipientsModal.scss";

const recipientTypeLabel = {
  admin: "Admins",
  booker: "Booker",
  traveller: "Traveller",
  guest: "Guests",
};

const NotifyRecipientsModal = ({
  open,
  toggle,
  navigate,
  tripId,
  notificationType = "bookingConfirmation",
}) => {
  const dispatch = useDispatch();
  const [selectedKeys, setSelectedKeys] = useState([]);
  // step: "preview" -> shown first (Email/SMS preview, Next button)
  //       "select"  -> recipient selection (Send button)
  const [step, setStep] = useState("preview");

  const recipientsResponse = useSelector(
    (state) => state?.getNotificationRecipients?.notificationRecipients?.data,
  );
  const recipientsLoading = useSelector(
    (state) => state?.getNotificationRecipients?.loading,
  );
  const sendLoading = useSelector((state) => state?.sendNotifications?.loading);

  const recipients = recipientsResponse?.recipients || [];
  const templatePreview = recipientsResponse?.templatePreview;

  useEffect(() => {
    if (open) {
      setStep("preview");
      setSelectedKeys([]);
      dispatch(clearNotificationRecipients());
    }
  }, [open]);

  useEffect(() => {
    if (open && tripId && notificationType) {
      dispatch(getNotificationRecipients(tripId, notificationType));
    }
  }, [open, tripId, notificationType]);

  useEffect(() => {
    if (open && recipients?.length > 0) {
      const defaultKeys = recipients
        .filter((r) => r.isDefaultSelected)
        .map((r) => r.key);
      setSelectedKeys(defaultKeys);
    }
  }, [recipientsResponse]);

  const groupedRecipients = useMemo(() => {
    return recipients.reduce((acc, recipient) => {
      const type = recipient.recipientType;
      if (!acc[type]) acc[type] = [];
      acc[type].push(recipient);
      return acc;
    }, {});
  }, [recipients]);

  const hasAdminOrBooker = selectedKeys.some((key) =>
    recipients.find(
      (r) => r.key === key && ["admin", "booker"].includes(r.recipientType),
    ),
  );
  const hasTravellerOrGuest = selectedKeys.some((key) =>
    recipients.find(
      (r) => r.key === key && ["traveller", "guest"].includes(r.recipientType),
    ),
  );

  const handleToggle = (key) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleCopy = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  // Top-right X icon -> just closes & navigates, never sends
  const handleClose = () => {
    setSelectedKeys([]);
    setStep("preview");
    dispatch(clearNotificationRecipients());
    toggle();
    if (navigate) {
      navigate("/reservation");
    }
  };

  const handleNext = () => {
    setStep("select");
  };

  const handleSend = () => {
    if (selectedKeys.length === 0) {
      toast.error("Please select at least one recipient");
      return;
    }
    dispatch(
      sendNotifications(tripId, notificationType, selectedKeys, () => {
        toast.success("Notification sent successfully");
        handleClose();
      }),
    );
  };

  return (
    <Modal
      open={open || false}
      aria-labelledby="notify-recipients-modal"
      className="customModal"
    >
      <Box className="modalContent width600 notifyRecipientsModal">
        <Box className="closeIcon justifyEnd">
          <IconButton aria-label="close" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="head3" className="fw600 colorPrimary mb10">
          Notify Recipients
        </Typography>

        {recipientsLoading ? (
          <BackDrop />
        ) : step === "preview" ? (
          <>
            <Box
              className="templatePreviewBox mb10"
              sx={{
                flex: 1,
                overflowY: "auto",
                minHeight: 0,
              }}
            >
              {templatePreview && (
                <>
                  <Typography variant="body1" className="fw600 colorBlack mb10">
                    Email Preview
                  </Typography>
                  <Typography variant="body2" className="colorGrey mb10">
                    Subject: {templatePreview?.email?.subject}
                  </Typography>

                  {hasAdminOrBooker && (
                    <Box className="mb10">
                      <Grid
                        container
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          variant="body3"
                          className="fw500 colorBlack"
                        >
                          For Admin/Booker
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleCopy(
                              `${templatePreview?.email?.subject}\n\n${templatePreview?.email?.bookerAndAdminMessage}`,
                              "Admin/Booker email content",
                            )
                          }
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                      <Typography
                        variant="body3"
                        className="colorGrey whiteSpacePreLine"
                      >
                        {templatePreview?.email?.bookerAndAdminMessage}
                      </Typography>
                    </Box>
                  )}

                  {hasTravellerOrGuest && (
                    <Box className="mb10">
                      <Grid
                        container
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          variant="body3"
                          className="fw500 colorBlack"
                        >
                          For Traveller/Guest
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleCopy(
                              `${templatePreview?.email?.subject}\n\n${templatePreview?.email?.travellerAndGuestMessage}`,
                              "Traveller/Guest email content",
                            )
                          }
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                      <Typography
                        variant="body3"
                        className="colorGrey whiteSpacePreLine"
                      >
                        {templatePreview?.email?.travellerAndGuestMessage}
                      </Typography>
                    </Box>
                  )}

                  <Divider className="mb10" />

                  <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body1" className="fw600 colorBlack">
                      SMS Preview
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleCopy(templatePreview?.sms?.text, "SMS content")
                      }
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                  <Typography
                    variant="body3"
                    className="colorGrey mb10 whiteSpacePreLine"
                  >
                    {templatePreview?.sms?.text}
                  </Typography>
                </>
              )}
            </Box>

            <Grid container justifyContent="flex-end" className="mt10">
              <CommonButton
                name="Next"
                btnClass="btnMedium"
                onClick={handleNext}
                disabled={recipients.length === 0}
              />
            </Grid>
          </>
        ) : (
          <>
            <Typography variant="body3" className="colorGrey mb10 notifyNote">
              Note: SMS will be sent only if SMS is enabled for this event in
              company settings.
            </Typography>

            <Box
              className="recipientListBox heightModelScroll"
              sx={{
                flex: 1,
                overflowY: "auto",
                minHeight: 0,
              }}
            >
              {Object.keys(groupedRecipients).map((type) => (
                <Box key={type} className="mb10">
                  <Typography variant="body1" className="fw600 colorBlack mb10">
                    {recipientTypeLabel[type] || type}
                  </Typography>
                  {groupedRecipients[type].map((recipient) => (
                    <Grid
                      container
                      alignItems="center"
                      key={recipient.key}
                      className="recipientRow"
                    >
                      <Checkbox
                        checked={selectedKeys.includes(recipient.key)}
                        onChange={() => handleToggle(recipient.key)}
                      />
                      <Box>
                        <Typography
                          variant="body2"
                          className="fw500 colorBlack"
                        >
                          {recipient.name}
                        </Typography>
                        <Typography variant="body3" className="colorGrey">
                          {recipient.email}
                          {recipient.mobile ? ` | ${recipient.mobile}` : ""}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Box>
              ))}
              {recipients.length === 0 && (
                <Typography variant="body2" className="colorGrey">
                  No recipients found
                </Typography>
              )}
            </Box>

            <Grid container justifyContent="flex-end" className="mt10">
              <CommonButton
                name="Send"
                btnClass="btnMedium"
                onClick={handleSend}
                disabled={sendLoading || recipients.length === 0}
              />
            </Grid>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default NotifyRecipientsModal;
